import { apiService } from '@/shared/services/apiServices';
import { AxiosError } from 'axios';
import userProfileEndpoints from './userProfileEndpoints';
import { handleUserProfileError } from '../errors';

// ============================================================================
// Types
// ============================================================================

export type ProfileRole = 'SUPER_ADMIN' | 'ADMIN' | 'CLIENT' | 'TECHNICIAN';

export interface ProfileAddress {
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  zipCode: string | null;
}

export interface ProfilePicture {
  url: string;
  isUploaded: boolean;
  fileName?: string;
  filename?: string;
}

export interface ProfileDoc {
  url: string;
  filename: string;
  fileType: string;
  fileSize: number;
  isUploaded: boolean;
  path?: string;
  resourceId?: string;
}

export interface ProfileSignature {
  url: string;
  isUploaded?: boolean;
  /** S3 object key — present for TECHNICIAN role. */
  path?: string;
  /** Media resource UUID — present for TECHNICIAN role. */
  resourceId?: string;
  filename: string;
  fileType?: string;
  fileSize?: number;
}

export interface TechnicianPrivilegePermission {
  key: string;
  name: string;
  allowed: boolean;
}

export interface TechnicianPrivilegeModule {
  key: string;
  name: string;
  permissions: TechnicianPrivilegePermission[];
  fixedPermissions?: TechnicianPrivilegePermission[];
}

export interface ProfileSubscriptionDetails {
  planName: string | null;
  paymentStatus: string | null;
  expiryDate: string | null;
}

export interface ProfilePermissionEntry {
  key: string;
  name: string;
  allowed: boolean;
}

export interface ProfilePrivilegeEntry {
  key: string;
  name: string;
  permissions: ProfilePermissionEntry[];
}

export interface ProfileAssignedPermissions {
  templateName: string;
  privileges: ProfilePrivilegeEntry[];
}

export interface UserProfile {
  /** Internal UUID — present for TECHNICIAN role only. */
  id?: string;
  name: string;
  email: string;
  role: ProfileRole;
  status: string;
  phoneNumber: string | null;
  countryCode: string | null;
  address: ProfileAddress | null;
  profilePicture: ProfilePicture | null;
  signature: ProfileSignature | null;
  createdAt: string;
  updatedAt: string;

  // Technician-only fields
  privileges?: TechnicianPrivilegeModule[];

  // Client-only fields
  clientId?: string;
  contractDetails?: { docs: ProfileDoc[] };
  nip?: string;
  subscriptionDetails?: ProfileSubscriptionDetails | null;
  preferredCurrency?: string;
  assignedPermissions?: ProfileAssignedPermissions | null;
}

export interface ViewProfileResult {
  success: boolean;
  data?: UserProfile;
  error?: string;
}

// ============================================================================
// API
// ============================================================================

/**
 * Fetches the current user's profile via the unified endpoint.
 * The backend resolves the caller's identity and role from the JWT.
 * Endpoint: GET loopay-user-management/profile
 */
export const fetchProfile = async (): Promise<ViewProfileResult> => {
  try {
    const result = await apiService.getAPI(userProfileEndpoints.VIEW_PROFILE);

    if (!result.error) {
      const response = result.data as { profile: UserProfile };
      return { success: true, data: response.profile };
    }

    const { errorMessage } = handleUserProfileError(
      result.status,
      result.data,
      result.isNetworkError
    );
    return { success: false, error: errorMessage };
  } catch (error) {
    if (error instanceof AxiosError) {
      const { errorMessage } = handleUserProfileError(
        error.response?.status,
        error.response?.data,
        !error.response && !!error.request
      );
      return { success: false, error: errorMessage };
    }

    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'An unexpected error occurred.',
    };
  }
};
